import { useEffect, useRef, useState } from "react";

import { PopulationBasedFundingCollapsibleEventDetails } from "@/Common/Components/PopulationBasedFunding/PopulationBasedFundingCollapsibleEventDetails";
import { PopulationBasedFundingRegistrationPopup } from "@/Common/Components/PopulationBasedFunding/PopulationBasedFundingRegistrationPopup";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Guid } from "@/Common/Models/Guid";
import { Patient } from "@/Common/Models/Patient/Patient";
import { PopulationBasedFundingEventWithNotesModel } from "@/Common/Models/PopulationBasedFunding/PopulationBasedFundingEventWithNotesModel";
import { PopulationBasedFundingFormData } from "@/Common/Models/PopulationBasedFunding/PopulationBasedFundingFormData";
import { PopulationBasedFundingActions } from "@/Common/Services/PopulationBasedFunding/PopulationBasedFundingService";
import { PopulationBasedFundingPortfolioOverridePopup } from "@/Portfolio/Components/PopulationBasedFunding/PopulationBasedFundingPortfolioOverridePopup";
import { PopulationBasedFundingPatientHistoryItemDeletePopup } from "./PopulationBasedFundingPatientHistoryItemDeletePopup";
import { PopulationBasedFundingPatientHistoryItemEditPopup } from "./PopulationBasedFundingPatientHistoryItemEditPopup";

import styles from "./PopulationBasedFundingPatientHistory.module.scss";

interface IPopulationBasedFundingPatientHistoryProps {
    patientModel: Patient
}

function PopulationBasedFundingPatientHistory(props: IPopulationBasedFundingPatientHistoryProps): JSX.Element {
    const [itemToDelete, setItemToDelete] = useState<Guid | string>();
    const [itemToEdit, setItemToEdit] = useState<PopulationBasedFundingEventWithNotesModel>();
    const [proposalToOverride, setProposalToOverride] = useState<PopulationBasedFundingEventWithNotesModel>();
    const [internalProposalToRegister, setInternalProposalToRegister] = useState<PopulationBasedFundingEventWithNotesModel>();
    const patientHistoryItems = props.patientModel.PopulationBasedFunding?.getSortedHistory();
    const previousHistoryItemIds = useRef([]);
    const currentHistoryItemIds = props.patientModel.PopulationBasedFunding?.getSortedHistory()?.map(p => p.Id);
    const extendedEventsIds = props.patientModel.PopulationBasedFunding?.extendedEventIds;

    const [actionToRun, setActionToRun] = useState<() => Promise<void>>(null);

    const { getText } = useTranslation();

    const { ContactFacet_id } = { ...props.patientModel.PatientResponse };

    const areActionsDisabled = !!actionToRun;

    useEffect(
        () => onPatientChanged(),
        [ContactFacet_id]
    );

    useEffect(
        () => onHistoryListChanged(),
        [currentHistoryItemIds]
    );

    const onPatientChanged = (): void => {
        if (!extendedEventsIds) {
            PopulationBasedFundingActions.loadPatientHistoryAndExpandAll(props.patientModel);
        }
        else {
            PopulationBasedFundingActions.loadPatientHistory(props.patientModel);
        }

        previousHistoryItemIds.current = currentHistoryItemIds;
    };

    const onHistoryListChanged = (): void => {
        var addedEvents = currentHistoryItemIds?.filter(ev => !previousHistoryItemIds?.current?.includes(ev));
        if (addedEvents?.any()) {
            extendedEventsIds.push(...addedEvents);
        }

        previousHistoryItemIds.current = currentHistoryItemIds;
    };

    const expandCollapseAll = (): void => {
        PopulationBasedFundingActions.expandCollapseAllEvents(props.patientModel);
    };

    const expandCollapseOne = (item: PopulationBasedFundingEventWithNotesModel): void => {
        PopulationBasedFundingActions.expandCollapseOne(item, props.patientModel);
    };

    const showDeleteItemPopup = (item: PopulationBasedFundingEventWithNotesModel): void => {
        setItemToDelete(item.Id);
    };

    const onCancelDeleteItemPopupClick = (): void => {
        setItemToDelete(null);
    };

    const onConfirmDeleteItemPopupClick = async (): Promise<void> => {
        const response = await PopulationBasedFundingActions.deleteEvent(String(itemToDelete));

        if (response.IsSuccess) {
            PopulationBasedFundingActions.removeEventFromHistoryAndExtendedEventsList(String(itemToDelete), props.patientModel);
            PopulationBasedFundingActions.updateIcon(props.patientModel.InactivableResponse.Id);
        }

        if (response.Errors.any())
            sessionStore.showPopup(response.Errors);

        onCancelDeleteItemPopupClick();
    };

    const showEditItemPopup = (item: PopulationBasedFundingEventWithNotesModel): void => {
        setItemToEdit(item);
    };

    const closeEditItemPopup = (): void => {
        setItemToEdit(null);
    };

    const onRegisterFromInternalProposalClick = (event: PopulationBasedFundingEventWithNotesModel): void => {
        setInternalProposalToRegister(event);
    };

    const onSubmitPatientRegistration = async (formData: PopulationBasedFundingFormData): Promise<boolean> => {
        const response = await PopulationBasedFundingActions.acceptInternalRegistrationProposalAndUpdateHistory(
            internalProposalToRegister.Id,
            formData,
            props.patientModel
        );

        if (response?.IsSuccess) {
            PopulationBasedFundingActions.updateIcon(ContactFacet_id);
        }

        return response?.IsSuccess ?? false;
    };

    const onCancelFromInternalProposalClick = (eventId: string, patientContactFacetId: string): void => {
        const execute = async (): Promise<void> => {
            const response = await PopulationBasedFundingActions.deleteInternalRegistrationProposal(
                patientContactFacetId
            );

            if (response) {
                PopulationBasedFundingActions.deleteInternalRegistrationProposalFromPatientHistory(
                    props.patientModel,
                    eventId
                );
            }
        };
        setActionToRun(() => execute);
    };

    const onAcceptClick = (eventId: string, patientModel: Patient): void => {
        const execute = async (): Promise<void> => await PopulationBasedFundingActions.acceptProposal(eventId, patientModel);
        setActionToRun(() => execute);
    };

    const onOverrideClick = (event: PopulationBasedFundingEventWithNotesModel): void => {
        setProposalToOverride(event);
    };

    const saveOverrideRegistration = async (selectedReason: number, customReason: string, note: string, eventId: string): Promise<void> => {
        const response = await PopulationBasedFundingActions.overrideRegistrationProposal(
            selectedReason,
            customReason,
            note,
            eventId,
            props.patientModel
        );

        if (response?.IsSuccess) {
            PopulationBasedFundingActions.updateIcon(ContactFacet_id);
        }
    };

    const saveOverrideDeregistration = async (selectedReason: number, customReason: string, note: string, eventId: string): Promise<void> => {
        const response = await PopulationBasedFundingActions.overrideDeregistrationProposal(
            selectedReason,
            customReason,
            note,
            eventId,
            props.patientModel
        );

        if (response?.IsSuccess) {
            PopulationBasedFundingActions.updateIcon(ContactFacet_id);
        }
    };

    useEffect(
        () => {
            const execute = async (): Promise<void> => {
                if (actionToRun === null) {
                    PopulationBasedFundingActions.updateIcon(ContactFacet_id);
                }
                else {
                    await actionToRun?.();
                    setActionToRun(null);
                }
            };

            execute();
        },
        [actionToRun, ContactFacet_id]
    );

    const areAllItemsCollapsed = (extendedEventsIds?.length ?? 0) === 0;
    const showEditPopup = !!itemToEdit;
    const showDeletePopup = !!itemToDelete;
    const showOverridePopup = !!proposalToOverride;
    const showRegistrationPopup = !!internalProposalToRegister;

    if (!patientHistoryItems?.any())
        return <></>;

    return (
        <div className={`${styles.populatioBasedFundingPatientHistory} collapsible-list`}>
            <div className={`${styles.heading} ${styles.flexContainer}`}>
                <div>
                    <h3 className="h thin-font">{getText("RegistrationHistoryLabel")}</h3>
                </div>

                <div className={styles.headingLinks}>
                    <a onClick={() => expandCollapseAll()}>{areAllItemsCollapsed ? getText("ExpandAllLabel") : getText("CollapseAllLabel")}</a>
                </div>
            </div>
            <div className={styles.flexContainerItems}>
                {patientHistoryItems?.map(item => (
                    <PopulationBasedFundingCollapsibleEventDetails
                        key={String(item.Id)}
                        eventModel={item}
                        onDeleteClick={() => showDeleteItemPopup(item)}
                        onEditClick={() => showEditItemPopup(item)}
                        onAcceptClick={() => onAcceptClick(item.Id, props.patientModel)}
                        onOverrideClick={() => onOverrideClick(item)}
                        onRegisterFromInternalProposalClick={() => onRegisterFromInternalProposalClick(item)}
                        onCancelInternalProposalClick={() => onCancelFromInternalProposalClick(item.Id, item.PatientContactFacetId)}
                        onCollapseToggle={() => expandCollapseOne(item)}
                        isCollapsed={!extendedEventsIds?.contains(item.Id)}
                        disableActions={areActionsDisabled}
                    />
                ))}
            </div>
            {showDeletePopup && (
                <PopulationBasedFundingPatientHistoryItemDeletePopup
                    onCancel={() => onCancelDeleteItemPopupClick()}
                    onDelete={() => onConfirmDeleteItemPopupClick()}
                />
            )}
            {showEditPopup && (
                <PopulationBasedFundingPatientHistoryItemEditPopup
                    item={itemToEdit}
                    onCloseCallback={() => closeEditItemPopup()}
                    patientModel={props.patientModel}
                />
            )}
            {showOverridePopup && (
                <PopulationBasedFundingPortfolioOverridePopup
                    eventModel={proposalToOverride}
                    onClose={() => setProposalToOverride(null)}
                    onSaveRegistration={saveOverrideRegistration}
                    onSaveDeregistration={saveOverrideDeregistration}
                />
            )}
            {showRegistrationPopup && (
                <PopulationBasedFundingRegistrationPopup
                    currentStarredPhysician={props.patientModel?.HasDoctor()
                        ? props.patientModel?.GetAttendingDoctorOrSpecialistOnfileIntervenor()?.Employee.toPerson()
                        : null
                    }
                    onClose={() => setInternalProposalToRegister(null)}
                    onTrySubmit={onSubmitPatientRegistration}
                />
            )}
        </div>
    );
}

export { IPopulationBasedFundingPatientHistoryProps, PopulationBasedFundingPatientHistory };
